/**
 * JobHacki API client — keyless, public CORS JSON on jobhacki.com.
 *
 * Base URL precedence:
 *   1. window.JH_API_BASE      (injected by the jobhacki-site proxy in dev/staging)
 *   2. "https://jobhacki.com"  (default — same host as the deployed app)
 *
 * No build-time env variables are read, so the production bundle contains
 * zero references to any other host.
 */
function resolveBase() {
  if (typeof window !== "undefined" && typeof window.JH_API_BASE === "string") {
    const v = window.JH_API_BASE.replace(/\/$/, "");
    if (v) return v;
  }
  return "https://jobhacki.com";
}

const BASE = resolveBase();

/**
 * True when the URL has ?demo=… and we're not on jobhacki.com — preview-only.
 * Tools use this to short-circuit `login_required` with mock data so the UI
 * can be reviewed without a real session.
 */
export function isDemo() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "jobhacki.com" || host.endsWith(".jobhacki.com")) return false;
  return new URLSearchParams(window.location.search).has("demo");
}

async function jh(path, init = {}) {
  const url = `${BASE}${path}`;
  try {
    const res = await fetch(url, {
      credentials: "include",
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body && !init.headers?.["Content-Type"]
          ? { "Content-Type": "application/json" }
          : {}),
        ...(init.headers || {}),
      },
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON */
    }
    if (!res.ok && !data) return { ok: false, error: `http_${res.status}` };
    return data || { ok: res.ok };
  } catch {
    return { ok: false, error: "network" };
  }
}

/* ───── Auth ───── */
export const sendMagicLink = (email) =>
  jh("/api/lead", {
    method: "POST",
    body: JSON.stringify({ email, source: "login" }),
  });

export const getMe = () => jh("/api/me");
export const logout = () => jh("/api/logout");

/* ───── Jobs / Match / Readiness / Career ───── */
export const listJobs = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== "" && v != null)
  ).toString();
  return jh(`/api/jobs${q ? `?${q}` : ""}`);
};

export const matchResume = (payload) =>
  jh("/api/match", { method: "POST", body: JSON.stringify(payload) });

/**
 * Upload a PDF/DOC resume → POST /resume/parse (multipart, field 'file').
 * Returns {ok, text} when successful; the parsed plain text drops into the
 * Match textarea so the user can verify before scoring.
 * Uses raw fetch (not the jh() helper) because FormData needs the browser
 * to set its own multipart Content-Type (with a boundary).
 *
 * NOTE: do NOT use /resume/upload — that endpoint returns Resume Builder
 * JSON (sections, fields). We only want the text here.
 */
export const uploadResumeFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  try {
    const res = await fetch(`${BASE}/resume/parse`, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* ignore non-JSON */
    }
    if (!res.ok) return { ok: false, status: res.status, ...(data || {}) };
    return { ok: true, ...(data || {}) };
  } catch {
    return { ok: false, error: "network" };
  }
};

/* ───── Resume Builder save / load (paid) ─────
 * Endpoints documented in JOBHACKI_V2_BUGS §M:
 *   POST /resume-save  {name,data}      → {ok,id}
 *   GET  /resumes                       → {ok,resumes:[{id,name,updated_at}]}
 *   GET  /resume-load?id=<id>           → {ok,data}
 */
export const saveResume = (name, data) =>
  jh("/resume-save", { method: "POST", body: JSON.stringify({ name, data }) });
export const listResumes = () => jh("/resumes");
export const loadResume = (id) =>
  jh(`/resume-load?id=${encodeURIComponent(id)}`);

export const roleFitGet = (role) =>
  jh(`/api/rolefit${role ? `?role=${encodeURIComponent(role)}` : ""}`);

export const roleFitSave = (payload) =>
  jh("/api/rolefit", { method: "POST", body: JSON.stringify(payload) });

/* Backwards-compatible alias */
export const roleFit = roleFitSave;

/* ───── Stats ───── */
export const getMembers = () => jh("/api/members");
export const getRecent = () => jh("/api/recent");

/* ───── Public content feeds (no auth, no credentials) ───── */
const ASSETS = "https://storage.googleapis.com/jobhacki-assets/config";

async function publicJson(path) {
  try {
    const res = await fetch(`${ASSETS}/${path}`, { credentials: "omit" });
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    const data = await res.json();
    return { ok: true, data };
  } catch {
    return { ok: false, error: "network" };
  }
}

export const getPlaybooksFeed = () => publicJson("playbooks.json");
export const getToolboxFeed = () => publicJson("directory_seed.json");
export const getRolesFeed = () => publicJson("roles.json");

/* ───── Cross-device lists (jobhacki.com/api/me/lists) ───── */
export const getMyLists = () => jh("/api/me/lists");
export const saveMyLists = (lists) =>
  jh("/api/me/lists", { method: "POST", body: JSON.stringify(lists) });

/* ───── Growth wiring (JOBHACKI_V2_BUGS §N–§P) ─────
 * `/api/lead`       — email-gate a download/save action. {email,source,playbook?,job?}
 * `/api/track`      — fire-and-forget event tracking. {event,path,meta?}
 * `/api/jobcount`   — fresh hero count {total,display}. (Lighter than /api/jobs.)
 */
export const submitLead = (payload) =>
  jh("/api/lead", { method: "POST", body: JSON.stringify(payload) });

/**
 * Fire-and-forget event tracking. Same-origin POST to /api/track with the
 * session cookie attached so the backend can identify the member and stitch
 * marketing attribution server-side. Spec body shape: {event, path, meta}.
 *
 * - Never blocks UI. Errors are swallowed.
 * - We don't strip query-string params from `path` — `?se=`, `?utm_*` etc.
 *   must survive so the attribution is preserved.
 * - Skips the network call in preview (?demo=…) since the preview origin
 *   isn't same-site with jobhacki.com and would 401 every event.
 */
export const trackEvent = (event, meta = {}) => {
  try {
    if (typeof window === "undefined") return Promise.resolve({ ok: false });
    if (isDemo()) return Promise.resolve({ ok: true, skipped: "demo" });
    const path = window.location.pathname + window.location.search;
    fetch(`${BASE}/api/track`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, path, meta }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never block UI */
  }
  return Promise.resolve({ ok: true });
};

export const getJobCount = () => jh("/api/jobcount");

export { BASE as API_BASE };
